import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import Button from '../Button';
import { Icon } from '@iconify/react';
import FormBuilder, { FieldConfig } from '../FormBuilder';
import Modal from '../Modal';
import { createVehicle, deleteVehicle, updateVehicle } from '../../services/vehicleService';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

export type Loan = {
  loan_id?: number | string;
  loan_code?: string;
  borrower_id?: number | string;
  vehicle_id?: number | string;
  loan_amount?: string | number;
  status?: string;
  disbursement_date?: string | null;
  repayment_frequency?: string | null;
  interest_rate?: string | null;
  tenure_months?: number | null;
  emi_amount?: string | number | null;
  start_date?: string | null;
  end_date?: string | null;
};

export type Vehicles = {
  owner_id: string | number;
  vehicle_type: string;
  manufacturer: string;
  model: string;
  variant?: string | null;
  year_of_manufacture?: string | number | null;
  registration_number: string;
  chassis_number: string;
  engine_number: string;
  fuel_type?: string | null;
  vehicle_color?: string | null;
  seating_capacity?: number | null;
  purchase_date?: string | null; // YYYY-MM-DD
  insurance_expiry?: string | null; // YYYY-MM-DD
  hypothecated_to?: string | null;
  current_status?: string | null;
  created_by_role?: string | number | null;
  created_by_id?: string | number | null;
  vehicle_id?: number | string;
  id?: number | string;
  loan_status?: string[]; 
  loans?: Loan[]; 
  vehicle_front_url?:any;
  vehicle_back_url?:any;
  vehicle_left_url?:any;
  vehicle_right_url?:any;
  number_plate_url?:any;
  odometer_photo_url?:any;
}

type Props = {
  profileId: number | string;
  vehicles?: Vehicles[] | null;
  onChange?: (newList: Vehicles[]) => void;
}

function LabelValue({ label, value }: { label: string; value?: any }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start">
      <span className="font-medium text-gray-700 sm:w-32 md:w-36 lg:w-40 flex-shrink-0">
        {label}:
      </span>
      <span
        className="
          text-gray-800 
          flex-1 
          break-words 
          whitespace-pre-wrap 
          overflow-hidden 
          break-all
        "
      >
        {value || "—"}
      </span>
    </div>
  );
}

      const formatDate = (isoDate: string) => {
  if (!isoDate) return "";

  const date = new Date(isoDate);
if (isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export default function VehicleDetails({
  profileId,
  vehicles = [],
  onChange,
}: Props) {
  const [list, setList] = useState<Vehicles[]>(vehicles ?? []);

  // modal state for add/edit
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicles | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string[]> | null>(
    null
  );
  // delete-confirm modal state
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Vehicles | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);


  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const isCustomer = user?.role === "Customer";

  const navigate = useNavigate();

  useEffect(() => {
    setList(vehicles ?? []);
  }, [vehicles, profileId]);

  // fields for FormBuilder - adjust types/labels as you like
  const formFields: FieldConfig[] = [
    { name: "vehicle_type", label: "Vehicle Type", type: "chips", required: true ,options:["Two_wheeler","Auto","Car","Truck","Bus","Tractor","Commercial","Other"]},
    { name: "manufacturer", label: "Manufacturer", type: "text" ,placeholder:"Enter Manufacturer" , required: true },
    { name: "model", label: "Model", type: "text",placeholder:"Enter Model" , required: true },
    { name: "variant", label: "Variant", type: "text" ,placeholder:"Enter Variant"  },
    { name: "year_of_manufacture", label: "Year of Manufacture", type: "text",placeholder:"Enter Year of Manufacture" , minLength:4, maxLength:4},
    { name: "registration_number", label: "Registration Number", type: "text",placeholder:"Enter Registration Number" , required: true },
    { name: "chassis_number", label: "Chassis Number", type: "text",placeholder:"Enter Chassis Number" },
    { name: "engine_number", label: "Engine Number", type: "text" ,placeholder:"Enter Engine Number"   },
    { name: "fuel_type", label: "Fuel Type", type: "chips" ,options:["Petrol","Diesel","Electric","Hybrid","CNG","LPG"] },
    { name: "vehicle_color", label: "Vehicle Color", type: "text" ,placeholder:"Enter Vehicle Color"  },
    { name: "seating_capacity", label: "Seating Capacity", type: "number" ,placeholder:"Enter Seating Capacity"},
    { name: "purchase_date", label: "Purchase Date", type: "date" ,placeholder:"Enter Purchase Date" },
    { name: "insurance_expiry", label: "Insurance Expiry", type: "date" ,placeholder:"Enter Insurance Expiry" },
    { sectionTitle: "Vehicle Images" },
    {
  name: "vehicle_front",
  label: "Vehicle Front",
  type: "file",
  accept: "image/*",
  previewUrlField: "vehicle_front_url",
},
{
  name: "vehicle_back",
  label: "Vehicle Back",
  type: "file",
  accept: "image/*",
  previewUrlField: "vehicle_back_url",
},
{
  name: "vehicle_left",
  label: "Vehicle Left",
  type: "file",
  accept: "image/*",
  previewUrlField: "vehicle_left_url",
},
{
  name: "vehicle_right",
  label: "Vehicle Right",
  type: "file",
  accept: "image/*",
  previewUrlField: "vehicle_right_url",
},
{
  name: "number_plate",
  label: "Chassis Number",
  type: "file",
  accept: "image/*",
  previewUrlField: "number_plate_url",
},
{
  name: "odometer_photo",
  label: "Odometer Photo",
  type: "file",
  accept: "image/*",
  previewUrlField: "odometer_photo_url",
},

  ];

  const openAdd = () => {
    setEditing(null);
    setFormErrors(null);
    setModalOpen(true);
  };

  const openEdit = (item: Vehicles) => {
    setEditing({ ...item }); // clone to avoid direct mutation
    setFormErrors(null);
    setModalOpen(true);
  };

  const handleSubmit = async (data: any) => {
    setSubmitting(true);
    setFormErrors(null);
    try {
      if (!editing) {
        data.owner_id = profileId;
      }

      // If caller passed a FormData, use it directly. Otherwise filter out
      // any file fields that are not actual File/FileList values when editing
      // (these may be preview URL strings like `vehicle_front_url`). The
      // customer edit form strips such non-file image fields before sending
      // to avoid server errors; mirror that behavior here.
      let submitData: any = data;
      if (!(data instanceof FormData)) {
        const fileFieldNames = formFields
          .filter((f: any) => f.type === "file")
          .map((f: any) => (f as any).name);

        // When editing, remove image keys that are not actual files
        if (editing) {
          const copy: any = { ...data };
          fileFieldNames.forEach((name) => {
            const v = copy[name];
            const isFile = v instanceof File || (v instanceof FileList && v.length > 0);
            if (!isFile) delete copy[name];
          });
          submitData = copy;
        }
      }

      const payload =
        submitData instanceof FormData
          ? submitData
          : (() => {
              const fd = new FormData();
              const fileFieldNames = formFields
                .filter((f: any) => f.type === "file")
                .map((f: any) => (f as any).name);
              const previewFieldNames = formFields
                .filter((f: any) => f.type === "file")
                .map((f: any) => (f as any).previewUrlField)
                .filter(Boolean);

              Object.entries(submitData).forEach(([k, v]: any) => {
                if (v === undefined || v === null) return;

                // always skip preview URL fields
                if (previewFieldNames.includes(k)) return;

                // handle file fields: only append when an actual File/FileList is provided
                if (fileFieldNames.includes(k)) {
                  if (v instanceof FileList && v.length) fd.append(k, v[0]);
                  else if (v instanceof File) fd.append(k, v);
                  // otherwise skip string/URL values
                  return;
                }

                if (v instanceof FileList && v.length) fd.append(k, v[0]);
                else if (v instanceof File) fd.append(k, v);
                else if (typeof v === "object") fd.append(k, JSON.stringify(v));
                else fd.append(k, String(v));
              });
              return fd;
            })();

      // add new
      if (!editing) {
        const res = await createVehicle(payload);
        const created: Vehicles = res?.vehicles ?? res?.data ?? res;
        const newList = [created, ...list];
        setList(newList);
        onChange?.(newList);
        toast.success(res?.message || "Vehicle added.");
      } else {
        const vehicleId = (editing as any).id ?? (editing as any).vehicle_id;
        const res = await updateVehicle(vehicleId, payload);
        const updated = res?.vehicles ?? res?.data ?? { ...editing, ...data };
        const newList = list.map((it) => {
          const itId = (it as any).id ?? (it as any).vehicle_id;
          const updatedId = (updated as any).id ?? (updated as any).vehicle_id;
          return itId === updatedId ? updated : it;
        });
        setList(newList);
        onChange?.(newList);
        toast.success(res?.message || "Vehicle updated.");
      }

      setModalOpen(false);
    } catch (err: any) {
      const errorData = err?.response?.data;
      setFormErrors(errorData?.errors ?? null);
      toast.error(errorData?.message || "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  };

  // open confirm modal (instead of immediate delete)
  const handleOpenDeleteModal = (item: Vehicles) => {
    setSelectedForDelete(item);
    setConfirmDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteModalOpen(false);
    setSelectedForDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedForDelete) return;
    const vehicleId = (selectedForDelete as any).id ?? (selectedForDelete as any).vehicle_id;
    if (!vehicleId) {
      toast.error("Invalid vehicle id");
      return;
    }

    setDeleting(true);
    try {
      const res = await deleteVehicle(vehicleId);
      const newList = list.filter(
        (it) =>
          (it as any).id !== vehicleId &&
          (it as any).vehicle_id !== vehicleId
      );
      setList(newList);
      onChange?.(newList);
      toast.success(res?.message || "Deleted successfully.");
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete.");
    } finally {
      setDeleting(false);
      setConfirmDeleteModalOpen(false);
      setSelectedForDelete(null);
    }
  };

  // helper to map loan status to badge classes
  const getStatusClasses = (status?: string) => {
    const s = String(status || '').toLowerCase();
    if (s === 'active' || s === 'inprogress' || s === 'in_progress') {
      return "text-yellow-800 bg-yellow-100";
    }
    if (s === 'completed' || s === 'closed') {
      return "text-green-800 bg-green-100";
    }
    if (s === 'settlement' || s === 'settled' || s === 'settling') {
      return "text-blue-800 bg-blue-100";
    }
    if (s === 'default' || s === 'pending') {
      return "text-rose-800 bg-rose-100";
    }
    // fallback
    return "text-gray-700 bg-gray-100";
  };

  return (
    <div>
      <div className="flex flex-row justify-between items-center mb-5 gap-3">
        <h3 className="text-lg md:text-xl font-semibold text-gray-800 ">
         Vehicle
        </h3>
       {!isCustomer &&( <Button
          onClick={openAdd}
          className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm md:text-base"
        >
          <Icon icon="mdi:plus" width={18} />
          Add
        </Button>)}
      </div>
      <div className="space-y-5">
        {list.length > 0 ? (
          list.map(( item, idx)=>(
            <div 
             key={(item as any).id ?? (item as any).vehicle_id ?? idx}
             className="bg-white p-5 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg">
              {/* Header */}
              <div className="flex  flex-row items-center justify-between pb-3 mb-2 gap-3 border-b border-gray-200">
                <h4  className="font-semibold text-base md:text-lg text-gray-800 flex gap-4">
                  Vehicle
                  {!isCustomer &&(
                    <div>
                      <p className='text-xs text-gray-400 me-2'>Created by : {item.created_by_role} </p>
                      <p className='text-xs text-gray-400'> ID:{item.created_by_id} </p>
                    </div>
                  )}
                </h4>

                <div className="flex flex-wrap items-center gap-2">
                  {!isCustomer &&(<button
                    onClick={() => openEdit(item)}
                    className="text-blue-600 border border-blue-400 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-blue-50 text-sm md:text-base transition"
                  >
                    <Icon icon="mingcute:edit-line" className="text-md md:text-lg" />
                  </button>)}

                  { isAdmin &&( <button
                    onClick={() => handleOpenDeleteModal(item)}
                    className="text-rose-600 border border-rose-400 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-rose-50 text-sm md:text-base transition"
                  >
                    <Icon icon="fluent:delete-24-regular" className="text-md md:text-lg" />
                  </button>)}
                </div>
              </div>

              {/* Content */}
              <div className="grid grid-cols-1  lg:grid-cols-2 gap-8">
                <div className="grid  span-cols-2  gap-y-4 text-wrap">
                  <LabelValue label="Vehicle type" value={item?.vehicle_type } />
                  <LabelValue label="Manufacturer" value={item.manufacturer} />
                  <LabelValue label="Model" value={item.model} />
                  <LabelValue label="Variant" value={item.variant} />
                  <LabelValue label="Year of Manufacture " value={item.year_of_manufacture} />
                  <LabelValue label="Fuel type" value={item.fuel_type} />
                  <div>
                  </div>
                </div>

                <div className="grid span-cols-2 gap-y-4 mt-4 lg:mt-0 text-wrap">
                  <LabelValue label="Registration Number" value={item.registration_number} />
                  <LabelValue label="chassis_number" value={item.chassis_number} />
                  <LabelValue label="engine_number" value={item.engine_number} />
                  <LabelValue label="vehicle_color" value={item.vehicle_color} />
                  <LabelValue label="seating_capacity" value={item.seating_capacity} />
                  <LabelValue label="purchase_date" value={item.purchase_date ? formatDate(item.purchase_date) : "—"} />
                  <LabelValue label="insurance_expiry"value={item.insurance_expiry ? formatDate(item.insurance_expiry) : "—"}/>
                  {/* <LabelValue label="hypothecated_to" value={item.hypothecated_to} /> */}
                </div>
                
              </div>
              {/* Vehicle Images */}
<div className="mt-6 border-t border-gray-200 pt-4">
  <h5 className="font-medium text-gray-800 mb-3">Vehicle Images</h5>

 {!item.vehicle_front_url && !item.vehicle_back_url &&  !item.vehicle_left_url && !item.vehicle_right_url && !item.number_plate_url&& !item.odometer_photo_url &&( 
  <div className='flex text-center items-center justify-center text-gray-400'>
    <p>No Images Available</p>
  </div>
  )}

  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
    {item.vehicle_front_url && (
      <div className="flex flex-col items-center gap-2">
        <div className="w-32 h-24 rounded-lg overflow-hidden border bg-gray-50">
          <img
            src={item.vehicle_front_url}
            alt="Vehicle Front"
            className="w-full h-full object-cover cursor-pointer"
             onClick={() => setPreviewImage(item.vehicle_front_url)}
          />
        </div>
        <span className="text-xs text-gray-600">Front</span>
      </div>
    )}

    {item.vehicle_back_url && (
      <div className="flex flex-col items-center gap-2">
        <div className="w-32 h-24 rounded-lg overflow-hidden border bg-gray-50">
          <img
            src={item.vehicle_back_url}
            alt="Vehicle Back"
            className="w-full h-full object-cover cursor-pointer"
             onClick={() => setPreviewImage(item.vehicle_back_url)}
          />
        </div>
        <span className="text-xs text-gray-600">Back</span>
      </div>
    )}

    {item.vehicle_left_url && (
      <div className="flex flex-col items-center gap-2">
        <div className="w-32 h-24 rounded-lg overflow-hidden border bg-gray-50">
          <img
            src={item.vehicle_left_url}
            alt="Vehicle Left"
            className="w-full h-full object-cover cursor-pointer"
             onClick={() => setPreviewImage(item.vehicle_left_url)}
          />
        </div>
        <span className="text-xs text-gray-600">Left</span>
      </div>
    )}

    {item.vehicle_right_url && (
      <div className="flex flex-col items-center gap-2">
        <div className="w-32 h-24 rounded-lg overflow-hidden border bg-gray-50">
          <img
            src={item.vehicle_right_url}
            alt="Vehicle Right"
            className="w-full h-full object-cover cursor-pointer"
             onClick={() => setPreviewImage(item.vehicle_right_url)}
          />
        </div>
        <span className="text-xs text-gray-600">Right</span>
      </div>
    )}

    {item.number_plate_url && (
      <div className="flex flex-col items-center gap-2">
        <div className="w-32 h-24 rounded-lg overflow-hidden border bg-gray-50">
          <img
            src={item.number_plate_url}
            alt="Chassis Number"
            className="w-full h-full object-cover cursor-pointer"
             onClick={() => setPreviewImage(item.number_plate_url)}
          />
        </div>
        <span className="text-xs text-gray-600">Chassis Number</span>
      </div>
    )}

    {item.odometer_photo_url && (
      <div className="flex flex-col items-center gap-2">
        <div className="w-32 h-24 rounded-lg overflow-hidden border bg-gray-50">
          <img
            src={item.odometer_photo_url}
            alt="Odometer"
            className="w-full h-full object-cover cursor-pointer"
            onClick={() => setPreviewImage(item.odometer_photo_url)}
          />
        </div>
        <span className="text-xs text-gray-600">Odometer</span>
      </div>
    )}
  </div>
</div>


              {/* Loans block */}
              { (item.loans && item.loans.length > 0) || (item.loan_status && item.loan_status.length > 0) ? (
                <div className="mt-4 border-t border-gray-400 pt-4">
                  <h5 className="font-medium mb-3">Loans</h5>

                  {/* detailed loans list */}
                  {item.loans && item.loans.length > 0 && (
                    <div className="space-y-3">
                      {item.loans.map((loan) => {
                        const status = loan.status ?? (item.loan_status ? item.loan_status[0] : undefined);
                        return (
                          <div key={(loan as any).loan_id ?? String(loan.loan_code ?? Math.random())} className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-gray-50 p-3 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <div className={`text-sm px-2 py-1 rounded-full font-medium ${getStatusClasses(status)}`}>
                                  {status ?? "Unknown"}
                                </div>
                                <div>
                                  <div className="text-sm font-semibold">Loan Code :{loan.loan_code ?? loan.loan_code ?? '—'}</div>
                                  <div className="text-xs text-gray-600">
                                    Amount: {loan.loan_amount ?? '—'} • Tenure: {loan.tenure_months ?? '—'} mo • EMI: {loan.emi_amount ?? '—'}
                                  </div>
                                  <p className="text-xs text-gray-700 mt-2 font-semibold" >End Date : {loan.end_date}</p>
                                </div>
                              </div>
                            </div>

                            <div className="flex-shrink-0 flex items-center gap-2 justify-end">
                              <Button
                                variant="outline"
                                onClick={() => navigate("/loan-dues", { state: { loan_id: loan.loan_id } })}
                                // className="px-3 py-1.5 rounded-lg border border-blue-300 hover:bg-blue-50 text-blue-600 text-sm"
                              >
                                View Loan
                              </Button>
                              {/* optional: quick nav to loan detail page if you have one */}
                              {/* <button className="px-3 py-1.5 rounded-lg bg-gray-100 text-sm">Details</button> */}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))
        ) : (
          <div className='text-center text-gray-500 bg-gray-50 p-6 rounded-xl shadow-sm'>
            No vehicle...
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Edit Vehicle" : "Add Vehicle"}
      >
        <FormBuilder
          defaultValues={editing ?? {}}
          fields={formFields}
          onSubmit={handleSubmit}
          onCancel={() => setModalOpen(false)}
          submitLabel={submitting ? "Saving..." : editing ? "Update" : "Add"}
          error={formErrors}
        />
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={confirmDeleteModalOpen}
        onClose={handleCancelDelete}
        title="Confirm delete"
      >
        <div className="py-2">
          <p className="mb-4">
            Are you sure you want to delete{" "}
            <strong>
              {selectedForDelete?.model ??
                selectedForDelete?.registration_number ??
                "this vehicle record"}
            </strong>
            ? This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancelDelete}
              className="px-4 py-2 rounded-lg border hover:bg-gray-100"
              disabled={deleting}
            >
              Cancel
            </button>

            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>
      {previewImage && (
  <div
    className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 transition duration-300 ease-in-out"
    onClick={() => setPreviewImage(null)}
  >
    <button
  className="absolute top-5 right-5 text-white text-3xl  cursor-pointer"
  onClick={() => setPreviewImage(null)}
>
  ✕
</button>

    <img
      src={previewImage}
      alt="Preview"
      className="max-h-[90%] max-w-[90%] rounded-lg shadow-lg"
    />
  </div>
)}

    </div>
  );
}
